"use client";

import { useRouter } from "next/navigation";
import { Trophy } from "lucide-react";
import { cn } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";

/** roundId("2024-1") → "2024년 1회" 표시 라벨. 형식이 다르면 원본 그대로. */
function formatRoundLabel(roundId: string): string {
  const m = /^(\d{4})-(\d+)$/.exec(roundId);
  return m ? `${m[1]}년 ${m[2]}회` : roundId;
}

/** roundId("2024-1") → "24-1" 컴팩트 라벨(작은 박스 셀용). 형식이 다르면 원본 그대로. */
function formatRoundLabelCompact(roundId: string): string {
  const m = /^(\d{4})-(\d+)$/.exec(roundId);
  return m ? `${m[1].slice(2)}-${m[2]}` : roundId;
}

/**
 * 기출 회차 완주 진행상황 카드 (JCG-2263, JCG-3284로 공용 추출).
 *
 * - 핵심 표시: 완주한 회차 수 / 전체 회차 수 + Progress 바
 * - 강조색은 violet/indigo("성취" 톤, RoundCompletionBadge·🏆와 통일) — 출석(amber/green)·
 *   정답률(green/amber/red) 통계와 시각적으로 구분.
 * - `detailed`(문제 탭): 전체 회차를 박스 그리드로 노출(완주=violet 채움+🏆, 미완주=빈 보더 박스).
 *   출석 그리드(SubjectProgressView의 AttendanceGrid)와 같은 구조를 미러링하되 색만 성취 톤으로.
 *   summary(전체 탭 / 이전 회차 다이얼로그): 진행바·요약만 — "전체"와 "문제"가 다르게 보여야
 *   한다는 요구(JCG-2263). summary 변형은 네비게이션·클릭이 없어 read-only 다이얼로그에서도 안전.
 * - 렌더 게이팅은 호출부에서 `showCompletion && totalRoundCount > 0`로 처리한다.
 *
 * StudyProgressPage(활성 페이지)와 PreviousSessionDialog(이전 회차 스냅샷)가 공유한다. 활성
 * 페이지는 summary/detailed 둘 다, 다이얼로그는 summary(detailed 미지정)만 사용한다(JCG-3284).
 */
export default function PastExamCompletionCard({
  completedRoundCount,
  totalRoundCount,
  completionByRound,
  allRounds,
  detailed = false,
}: {
  completedRoundCount: number;
  totalRoundCount: number;
  completionByRound: Record<string, number>;
  allRounds: string[];
  detailed?: boolean;
}) {
  const router = useRouter();
  const percent =
    totalRoundCount > 0 ? (completedRoundCount / totalRoundCount) * 100 : 0;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Trophy
            className="h-4 w-4 text-violet-600 dark:text-violet-400"
            aria-hidden="true"
          />
          기출 완주 현황
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* 진행바 요약 — "전체" 탭만. "문제" 탭(detailed)은 박스 그리드만 보여준다. JCG-2263 */}
        {!detailed && (
          <div>
            <div className="flex justify-between items-center mb-1">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                완주한 회차
              </span>
              <span className="text-sm font-medium text-violet-600 dark:text-violet-300">
                {completedRoundCount} / {totalRoundCount} 회차
              </span>
            </div>
            <Progress
              value={percent}
              aria-label={`기출 회차 완주 진행률 ${Math.round(percent)}%`}
              className="h-2 [&>[data-slot=progress-indicator]]:bg-gradient-to-r [&>[data-slot=progress-indicator]]:from-violet-600 [&>[data-slot=progress-indicator]]:to-indigo-600 dark:[&>[data-slot=progress-indicator]]:from-violet-500 dark:[&>[data-slot=progress-indicator]]:to-indigo-500"
            />
          </div>
        )}

        {/* 문제 탭(detailed): 전체 회차 박스 그리드(최신 회차가 좌상단 — allRounds 역순).
            완주 박스엔 past-exam 전체완주 뱃지 효과(.completion-allclear: 글로우 펄스 + 회전
            테두리 + 글로시 스윕)를 적용. JCG-2263 */}
        {detailed && (
          <div className="flex flex-wrap gap-2" aria-label="기출 회차 완주 현황">
            {[...allRounds].reverse().map((roundId) => {
              const count = completionByRound[roundId] ?? 0;
              const isCompleted = count > 0;
              const repeated = count > 1;
              const [year] = roundId.split("-");
              const ariaLabel = isCompleted
                ? `${formatRoundLabel(roundId)} 완주${repeated ? ` (${count}번 반복)` : ""}`
                : `${formatRoundLabel(roundId)} 미완주`;
              return (
                <button
                  key={roundId}
                  type="button"
                  title={formatRoundLabel(roundId)}
                  aria-label={ariaLabel}
                  onClick={() => router.push(`/past-exam/${year}/${roundId}`)}
                  className="group flex w-12 flex-col items-center gap-1 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
                >
                  {/* "N번 반복" 배지 — 박스 위 빈 공간. 미완주/1회 완주면 자리만 유지(레이아웃 정렬). */}
                  <span
                    className={cn(
                      "h-3.5 text-[9px] font-bold leading-3.5 tabular-nums",
                      repeated
                        ? "text-amber-700 dark:text-amber-400"
                        : "invisible"
                    )}
                    aria-hidden="true"
                  >
                    {repeated ? `${count}번 반복` : "·"}
                  </span>
                  {isCompleted ? (
                    // 전체완주 뱃지와 동일한 레이어 구조(글로우+회전 테두리+글로시 스윕).
                    <span
                      className="completion-allclear relative h-9 w-9 transition-transform group-hover:scale-110"
                      aria-hidden="true"
                    >
                      <span className="completion-allclear__ring" />
                      <span className="completion-allclear__core">
                        <span className="text-sm leading-none drop-shadow-[0_1px_1px_rgba(0,0,0,0.3)]">
                          🏆
                        </span>
                      </span>
                    </span>
                  ) : (
                    <span
                      className="flex h-9 w-9 items-center justify-center rounded-md border border-border bg-muted transition-transform group-hover:scale-110"
                      aria-hidden="true"
                    />
                  )}
                  <span
                    className={cn(
                      "text-[10px] leading-none tabular-nums",
                      isCompleted
                        ? "font-semibold text-violet-700 dark:text-violet-300"
                        : "text-gray-500 dark:text-gray-400"
                    )}
                    aria-hidden="true"
                  >
                    {formatRoundLabelCompact(roundId)}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
